import { MenuItem, Select, Stack } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import type { FieldConfiguration } from "../../../../../../../../api/types/fieldConfigurationTypes";
import { FormatType, PostConditionType } from "../../../../../../../../api/types/fieldConfigurationTypes";
import type { ReportField } from "../../../../../../../../api/types/svTypes";
import { isBoolean } from "../../../../utilities/isBoolean";
import SwitchMenuItem from "../../common/fields/field-options/SwitchMenuItem";

function PostFilteringMenuItem({
  field,
  saveChanges,
  readonly,
}: {
  field: ReportField;
  saveChanges: (changes: Partial<FieldConfiguration>, causeClosing: boolean) => void;
  readonly: boolean;
}) {
  const [postConditionType, setPostConditionType] = useState<PostConditionType>(
    field.config.postConditions?.[0]?.conditionType || PostConditionType.Is
  );
  const [postConditionValue, setPostConditionValue] = useState<boolean>(() =>
    isBoolean(field.config.postConditions?.[0]?.value) ? field.config.postConditions[0].value : true
  );
  const isPostConditionsEnabled = field.config.usePostConditions === true;

  useEffect(() => {
    if (!isPostConditionsEnabled) {
      return;
    }
    const timeout = setTimeout(() => {
      const changed =
        field.config.postConditions?.[0]?.conditionType !== postConditionType ||
        field.config.postConditions?.[0]?.value !== postConditionValue;
      if (changed) {
        saveChanges({ postConditions: [{ conditionType: postConditionType, value: postConditionValue }] }, false);
      }
    }, 0);
    return () => clearTimeout(timeout);
  }, [postConditionType, postConditionValue, saveChanges, field.config.postConditions, isPostConditionsEnabled]);

  const handleChangeState = useCallback(() => {
    const enabling = !field.config.usePostConditions;
    const postConditions = enabling ? [{ conditionType: postConditionType, value: postConditionValue }] : [];
    saveChanges({ usePostConditions: enabling, postConditions }, false);
  }, [saveChanges, field.config.usePostConditions, postConditionType, postConditionValue]);

  const isBooleanField =
    field.config.format?.formatType === FormatType.Boolean ||
    field.config.format?.formatType === FormatType.BlankIfFalse;
  if (!isBooleanField) {
    return null;
  }

  return (
    <>
      <SwitchMenuItem
        caption="Post Conditions"
        value={!!field.config.usePostConditions}
        onClick={handleChangeState}
        disabled={readonly}
      />
      {isPostConditionsEnabled && (
        <Stack direction="row" useFlexGap spacing={1} sx={{ pl: 4, pr: 2 }}>
          <Select
            fullWidth
            value={postConditionType}
            sx={{ ".MuiSelect-select": { py: 0.7 } }}
            onChange={(e) => setPostConditionType(e.target.value as PostConditionType)}
            disabled={readonly}
          >
            <MenuItem value={PostConditionType.Is}>Is</MenuItem>
            <MenuItem value={PostConditionType.IsNot}>Is Not</MenuItem>
          </Select>
          <Select
            fullWidth
            value={`${postConditionValue}`}
            sx={{ ".MuiSelect-select": { py: 0.7 } }}
            onChange={(e) => setPostConditionValue(e.target.value === "true")}
            disabled={readonly}
          >
            <MenuItem value={"true"}>Yes</MenuItem>
            <MenuItem value={"false"}>No</MenuItem>
          </Select>
        </Stack>
      )}
    </>
  );
}

export default PostFilteringMenuItem;
